import React, { useCallback, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, FormControl, IconButton, MenuItem, Select, SelectChangeEvent, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { WorkflowExecutionLog } from "fond/api";

import { LogContainer, LogContent, LogHeader, LogPanel } from "./LogViewer.styles";

export interface LogViewerProps {
  logs: WorkflowExecutionLog[];
  onDownload?: (log: WorkflowExecutionLog) => void;
  maxHeight?: number | string;
}

const LogViewer: React.FC<LogViewerProps> = ({ logs, onDownload, maxHeight = 400 }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const handleSelectChange = (event: SelectChangeEvent<number>) => {
    setSelectedIndex(event.target.value as number);
  };

  const handleCopy = useCallback(async () => {
    try {
      const content = logs[selectedIndex]?.Content;
      if (content) {
        await navigator.clipboard.writeText(content);
      }
    } catch (e) {
      enqueueSnackbar("Failed to copy log to clipboard.", { variant: "error" });
    }
  }, [selectedIndex, logs, enqueueSnackbar]);

  const handleDownload = useCallback(() => {
    if (onDownload && logs[selectedIndex]) {
      onDownload(logs[selectedIndex]);
    }
  }, [selectedIndex, logs, onDownload]);

  if (logs.length === 0) {
    return (
      <Box p={2}>
        <Typography color="text.secondary">No logs available</Typography>
      </Box>
    );
  }

  const selectedLog = logs[selectedIndex];

  return (
    <LogContainer>
      {logs.length > 1 && (
        <FormControl size="small" sx={{ minWidth: 200, m: 1 }}>
          <Select value={selectedIndex} onChange={handleSelectChange}>
            {logs.map((log, index) => (
              <MenuItem key={log.Filename} value={index}>
                {log.Filename}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <LogPanel>
        <LogHeader>
          {logs.length === 1 && <Typography variant="subtitle2">{selectedLog.Filename}</Typography>}
          {logs.length > 1 && <Box />}
          <Box>
            <Tooltip title="Copy to clipboard">
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onDownload && (
              <Tooltip title="Download log">
                <IconButton size="small" onClick={handleDownload}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </LogHeader>
        <LogContent style={{ maxHeight }}>{selectedLog.Content}</LogContent>
      </LogPanel>
    </LogContainer>
  );
};

export default LogViewer;
